import { useRef, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Fuel, Loader2 } from "lucide-react";

import { useTranslation } from "react-i18next";
const STATE_KEY = "hisabx.google.oauth.state";

/**
 * Direct Google OAuth callback. Google redirects here with `?code=...&state=...`.
 * We verify state (CSRF), exchange code with our backend, then either sign the
 * user in, route them to /register (invite), or /hello (lead capture).
 */
export default function AuthCallback() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    (async () => {
      const oauthError = params.get("error");
      const code = params.get("code");
      const state = params.get("state");

      if (oauthError) {
        setError(oauthError === "access_denied" ? "Sign-in cancelled." : `Google error: ${oauthError}`);
        return;
      }
      if (!code) {
        setError("Missing Google code — please try signing in again.");
        return;
      }

      // CSRF check
      const expectedState = (() => {
        try { return sessionStorage.getItem(STATE_KEY); } catch { return null; }
      })();
      if (expectedState && state && expectedState !== state) {
        setError("Security check failed (state mismatch). Please try again.");
        return;
      }
      try { sessionStorage.removeItem(STATE_KEY); } catch { /* ignore */ }

      const redirectUri = window.location.origin + "/auth/google";
      try {
        const { data } = await api.post("/auth/google/code", { code, redirect_uri: redirectUri });
        // Clear query so refreshes don't re-submit
        window.history.replaceState(null, "", window.location.pathname);

        if (data.needs_invite && data.invite?.code) {
          toast.info(t("auth.authcallback.invite_verified_finish_signing_up"));
          nav(`/register?code=${encodeURIComponent(data.invite.code)}`, { replace: true });
          return;
        }
        if (data.needs_intro && data.google) {
          const g = data.google;
          const q = new URLSearchParams({
            email: g.email || "",
            name: g.name || "",
            picture: g.picture || "",
            google_id: g.google_id || "",
          });
          nav(`/hello?${q.toString()}`, { replace: true });
          return;
        }
        if (data.ok && data.user) {
          await refresh();
          toast.success(`Welcome, ${data.user.name || "back"}!`);
          nav("/", { replace: true });
          return;
        }
        setError("Unexpected response — please try again.");
      } catch (err) {
        const msg = formatApiError(err);
        setError(msg);
        toast.error(msg);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-4">
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-lg">
        <Fuel className="h-7 w-7" />
      </div>
      {error ? (
        <>
          <div className="text-lg font-semibold text-foreground">{t("auth.authcallback.sign_in_failed")}</div>
          <div className="max-w-sm text-center text-sm text-muted-foreground">{error}</div>
          <button
            onClick={() => nav("/login", { replace: true })}
            className="mt-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90"
            data-testid="auth-callback-retry"
          >
            Back to sign-in
          </button>
        </>
      ) : (
        <>
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <div className="text-sm text-muted-foreground">Finishing Google sign-in…</div>
        </>
      )}
    </div>
  );
}
